/**
 * Chakra 6 — Ajna (§11). Element Licht, kleur indigo, mantra OM (852Hz).
 * Resource-label: Helderheid. Wereld: "De Vertroebelde Blik".
 *
 * Lapis Lazuli zet hier `veilImmuneTurns` direct (spec-vereenvoudiging, geen
 * status-object nodig). Orvath's zwakte verwijst naar Aardeanker — een ch1-
 * meesterschapskaart die tegen ch6 al beschikbaar is.
 */
import type { Chakra } from './types';
import { HEADERS } from './headers';

export const chakra6: Chakra = {
  header: HEADERS[6],
  wereld: 'De Vertroebelde Blik',
  codex:
    'Ajna, "opdracht/waarnemen", zit tussen de wenkbrauwen. Element Licht, kleur indigo, 2 bloemblaadjes, mantra OM (852Hz). Levensfase: adolescentie, eigen wereldbeeld vormen. Levensvraag: vertrouw ik wat ik zie en weet? Lichamelijk: hersenen, ogen, hypofyse (hormoonregie). Tekort: overweldigd door andermans waarheden, geen toegang tot intuïtie, overdenken zonder inzicht. Teveel: zwevend, te veel in fantasie/theorie, spiritueel ego, het echte leven vermijden via "hogere" verklaringen. Balans: heldere intuïtie naast logica, verbeeldingskracht die geworteld blijft, vertrouwen op je innerlijk kompas. Herstel: meditatie, visualisatie, dromen serieus nemen, eerlijk zelfonderzoek.',

  kaarten: [
    { id: 'ch6-amethist', naam: 'Amethist', type: 's', cost: 1, origin: 6, tekst: '+3 helderheid, neutraliseert sluieraanvallen', effect: { block: 3, cleanse: 'one' } },
    { id: 'ch6-fluoriet', naam: 'Fluoriet', type: 's', cost: 2, origin: 6, tekst: 'Logica/intuïtie balans: +2 effect deze beurt', effect: { buffNext: 2 } },
    { id: 'ch6-lapis-lazuli', naam: 'Lapis Lazuli', type: 's', cost: 1, origin: 6, tekst: 'Hand zichtbaar (2 beurten)', effect: { veilImmuneTurns: 2 } },
    { id: 'ch6-sodaliet', naam: 'Sodaliet', type: 's', cost: 2, origin: 6, tekst: 'Intuïtiekaarten -1 kosten (3 beurten)', effect: { costDownType: { type: 'p', amt: 1, turns: 3 } } },
    { id: 'ch6-indigo-kyaniet', naam: 'Indigo Kyaniet', type: 's', cost: 3, origin: 6, tekst: 'Alle Ajna-kaarten ×2', effect: { mult: { origin: 6, amt: 2 } } },
    { id: 'ch6-hypofyse-balans', naam: 'Hypofyse Balans', type: 'o', cost: 2, origin: 6, tekst: 'Neutraliseert hormonale aanvallen', effect: { cleanse: 'one' } },
    { id: 'ch6-oog-activatie', naam: 'Oog Activatie', type: 'o', cost: 1, origin: 6, tekst: '+2 energie, bekijk bovenste 3 deck-kaarten', effect: { energy: 2, peek: 3 } },
    { id: 'ch6-hersenen-balans', naam: 'Hersenen Balans', type: 'o', cost: 2, origin: 6, tekst: 'Volgende kaart +3 effect', effect: { buffNext: 3 } },
    { id: 'ch6-pijnappelklier', naam: 'Pijnappelklier', type: 'o', cost: 3, origin: 6, tekst: 'Hand permanent zichtbaar', effect: { clearVeilPermanent: true } },
    { id: 'ch6-meditatie', naam: 'Meditatie', type: 'p', cost: 2, origin: 6, tekst: 'Zolang actief: +2 helderheid/beurt, houdt veil weg', effect: { aura: { block: 2, cleanseVeil: true, name: 'Meditatie' } } },
    { id: 'ch6-visualisatie', naam: 'Visualisatie', type: 'p', cost: 1, origin: 6, tekst: 'Trek 1 bewust gekozen kaart', effect: { draw: 1 } },
    { id: 'ch6-droomwerk', naam: 'Droomwerk', type: 'p', cost: 2, origin: 6, tekst: 'Baas -6 HP, verwijder 1 illusieblokkade', effect: { dmg: 6, cleanse: 'one' } },
    { id: 'ch6-kaarsvlam-staren', naam: 'Kaarsvlam Staren', type: 'p', cost: 1, origin: 6, tekst: 'Trek 2 kaarten, +2 energie', effect: { draw: 2, energy: 2 } },
    { id: 'ch6-eerlijk-zelfonderzoek', naam: 'Eerlijk Zelfonderzoek', type: 'p', cost: 3, origin: 6, tekst: 'Alle blokkades weg, +5 HP', effect: { cleanse: 'all', heal: 5 } },
    { id: 'ch6-licht-element', naam: 'Licht Element', type: 'e', cost: 3, origin: 6, tekst: 'Alle Ajna-kaarten ×2', effect: { mult: { origin: 6, amt: 2 } } },
    { id: 'ch6-helder-zien', naam: 'Helder Zien', type: 'e', cost: 2, origin: 6, tekst: 'Alle sluiers weg, hand zichtbaar', effect: { cleanse: 'all', clearVeilPermanent: true }, meesterschap: true },
    { id: 'ch6-om-mantra', naam: 'OM Mantra', type: 'm', cost: 2, origin: 6, tekst: 'Doorbreekt illusie, +3 helderheid', effect: { cleanse: 'one', block: 3, tone: 852 } },
    { id: 'ch6-om-verdiept', naam: 'OM Verdiept', type: 'm', cost: 3, origin: 6, tekst: 'Alle blokkades weg, hand permanent zichtbaar', effect: { cleanse: 'all', clearVeilPermanent: true, tone: 852 } },
    { id: 'ch6-indigo-visualisatie', naam: 'Indigo Visualisatie', type: 'k', cost: 1, origin: 6, tekst: '+2 energie, intuïtiekaarten +1', effect: { energy: 2, buffNext: 1 } },
    { id: 'ch6-indigo-ademhaling', naam: 'Indigo Ademhaling', type: 'k', cost: 2, origin: 6, tekst: 'Trek 2 kaarten, 1 sluier-status -1 beurt', effect: { draw: 2, veilImmuneTurns: 1 } },
  ],

  bazen: [
    {
      id: 'ch6-veluun',
      naam: 'Veluun',
      titel: 'De Sluierlegger',
      hp: 28,
      energie: 2,
      aanvallen: [
        { naam: 'Sluier Opleggen', cost: 2, effect: { status: { kind: 'veil', turns: 2 } } },
        { naam: 'Vertroebeling', cost: 2, effect: { status: { kind: 'costUp', type: 'p', amt: 2, turns: 1 } } },
        { naam: 'Valse Waarneming', cost: 3, effect: { status: { kind: 'reverseNext' } } },
      ],
      zwaktes: [
        { card: 'Amethist', counters: 'Sluier Opleggen' },
        { card: 'Meditatie', clearsVeil: true },
        { card: 'Licht Element', counters: 'Vertroebeling' },
      ],
    },
    {
      id: 'ch6-rathom',
      naam: 'Rathom',
      titel: 'De Rationaliseerder',
      hp: 34,
      energie: 3,
      aanvallen: [
        { naam: 'Bewijs Het Maar', cost: 2, effect: { status: { kind: 'nullifyType', type: 'p', turns: 1 } } },
        { naam: 'Logica Dwang', cost: 3, effect: { conditionalDmg: { amt: 4, condition: 'maxCostNotPlayed' } } },
        { naam: 'Twijfel Zaaien', cost: 2, effect: { energyLoss: 2, discard: { n: 1, mode: 'top' } } },
      ],
      zwaktes: [
        { card: 'Fluoriet', counters: 'Logica Dwang' },
        { card: 'Droomwerk', bonusDmg: 6 },
        { card: 'OM Mantra', counters: 'Twijfel Zaaien' },
      ],
    },
    {
      id: 'ch6-orvath',
      naam: 'Orvath',
      titel: 'De Illusionist',
      hp: 32,
      energie: 3,
      aanvallen: [
        { naam: 'Illusie Voeden', cost: 2, effect: { dmg: 3 } },
        { naam: 'Ego Opblazen', cost: 3, effect: { forcePlayStrongest: true } },
        { naam: 'Valse Verlichting', cost: 3, effect: { status: { kind: 'lockType', type: 'e', turns: 1 } } },
      ],
      zwaktes: [
        { cards: ['Amethist', 'Fluoriet'], bonusDmg: 10, sameTurn: true },
        { card: 'Aardeanker', counters: 'Illusie Voeden' },
        { card: 'OM Mantra', counters: 'Valse Verlichting' },
      ],
    },
    {
      id: 'ch6-mavoreth',
      naam: 'Mavoreth',
      titel: 'De Blinde Ziener',
      hp: 70,
      energie: 5,
      eindbaas: true,
      passief:
        'Aan begin van elke speler-beurt: is er geen veil-immuniteit/cleanseVeil actief, dan herplaatst Mavoreth de veil voor die beurt (handkaarten tonen "?" tot ze gespeeld worden).',
      aanvallen: [
        { naam: 'Totale Verblinding', cost: 4, effect: { status: { kind: 'randomize', turns: 1 } } },
        { naam: 'Inzicht Vernietigen', cost: 3, effect: { dmg: 8 } },
        { naam: 'Derde Oog Sluiten', cost: 4, effect: { status: { kind: 'lockOrigin', origin: 6, turns: 2 } } },
        { naam: 'Labyrint', cost: 3, effect: { status: { kind: 'drawFromDiscard', turns: 1 } } },
      ],
      combo: { bossHpLoss: 25 },
      zwaktes: [
        { cards: ['OM Mantra', 'Amethist'], clearsVeil: true, sameTurn: true },
        { card: 'Zuivere Stem', counters: 'Totale Verblinding' },
      ],
      unlock: 'ch6-helder-zien',
      leermoment:
        'Mavoreth toont het gevaar van kennis zonder wijsheid: alles zien en niets begrijpen. Helderheid komt niet van meer informatie, maar van de stilte waarin je eigen waarheid kan opklinken. Herstel: minder consumeren, meer mediteren, je intuïtie vertrouwen vóór de data.',
    },
  ],
};
